import _ from "lodash";
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { DEFAULT_FILTERS, FILTER_TYPES } from "../modules/Constants";
import {
  getCurrentFiltersFromFilterKeys,
  getFilterStateFromVariable,
} from "../modules/Utils";

const { SEARCH } = FILTER_TYPES;
const RESET_FILTERS = "RESET_FILTERS";
const APPLY_FILTERS = "APPLY_FILTERS";

const reducer = (state, action) => {
  switch (action.type) {
    case SEARCH:
      return {
        ...state,
        [SEARCH]: action.payload,
      };

    case APPLY_FILTERS:
      return {
        ...state,
        ...(action.payload || {}),
      };

    case RESET_FILTERS: {
      const updatedFilters = { ...state };
      _.forEach(action.payload, filterKey => {
        updatedFilters[filterKey] = DEFAULT_FILTERS[filterKey];
      });
      return updatedFilters;
    }

    default:
      return state;
  }
};

const EMPTY_OBJ = {};

const FilterContext = createContext();

const FilterProvider = memo(
  ({ children, initialFilters = EMPTY_OBJ, dynamicFilters = EMPTY_OBJ }) => {
    const mergedInitialFilters = useMemo(() => {
      const initialFilterState = getFilterStateFromVariable({
        filters: initialFilters,
      });
      return _.merge({}, DEFAULT_FILTERS, initialFilterState);
    }, [initialFilters]);

    const [state, dispatch] = useReducer(reducer, mergedInitialFilters);

    const filters = useMemo(() => {
      const gridFilters = getCurrentFiltersFromFilterKeys({ filters: state });
      return { ...gridFilters, ...dynamicFilters };
    }, [state, dynamicFilters]);

    const initialFilterVariables = useMemo(() => {
      return getCurrentFiltersFromFilterKeys({ filters: mergedInitialFilters });
    }, [mergedInitialFilters]);

    const handleFilterUpdate = useCallback(
      (type, value) => dispatch({ type, payload: value }),
      []
    );

    const handleApplyFilters = useCallback(
      value => handleFilterUpdate(APPLY_FILTERS, value),
      [handleFilterUpdate]
    );

    const resetFilters = useCallback(
      ({ filtersToReset = _.values(FILTER_TYPES) }) => {
        dispatch({ type: RESET_FILTERS, payload: filtersToReset });
      },
      []
    );

    const isDefaultFiltersApplied = useMemo(
      () => _.isEqual(state, DEFAULT_FILTERS),
      [state]
    );

    return (
      <FilterContext.Provider
        value={{
          filters,
          state,
          handleFilterUpdate,
          isDefaultFiltersApplied,
          resetFilters,
          handleApplyFilters,
          defaultFilters: DEFAULT_FILTERS,
          initialFilterVariables,
        }}
      >
        {children}
      </FilterContext.Provider>
    );
  }
);

FilterProvider.displayName = "FilterProvider";

const useFilters = () => {
  return useContext(FilterContext);
};

const FilterConsumer = FilterContext.Consumer;

export { FilterProvider, FilterConsumer, useFilters };
